import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ChevronDown, ChevronRight, Pencil, Plus, Shield, X } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../context/useI18n';

const DOMAIN_LABELS = {
  platform: 'Plataforma',
  d28d: 'D28D',
  training: 'Training',
  food: 'FOOD',
  gym: 'Gimnasio',
  consumer: 'Consumidor',
};

const DOMAIN_OPTIONS = [
  'platform', 'd28d', 'training', 'food', 'gym', 'consumer',
];

const MODULE_OPTIONS = [
  { key: 'gym', label: 'Gimnasio' },
  { key: 'd28d', label: 'D28D' },
  { key: 'training', label: 'Training' },
  { key: 'food_plan', label: 'Plan alimentación' },
  { key: 'live_classes', label: 'Clases en vivo' },
];

function moduleSummary(access = {}) {
  const keys = Object.entries(access || {})
    .filter(([, v]) => v === true || (typeof v === 'string' && v.trim()))
    .map(([k]) => k);
  return keys.length ? keys.join(', ') : '—';
}

function emptyRoleForm() {
  return {
    code: '',
    label: '',
    domain: 'platform',
    permissions: [],
    module_access: {},
  };
}

function roleToForm(role) {
  return {
    code: role.code || '',
    label: role.label || role.name || '',
    domain: role.domain || 'platform',
    permissions: Array.isArray(role.permissions) ? [...role.permissions] : [],
    module_access: { ...(role.module_access || {}) },
    is_system: role.is_system !== false,
  };
}

function RoleEditorModal({
  open,
  mode,
  form,
  setForm,
  permissions,
  saving,
  error,
  onClose,
  onSave,
}) {
  const { t } = useI18n();
  const isCreate = mode === 'create';

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, saving, onClose]);

  const permsByDomain = useMemo(() => {
    const map = {};
    for (const p of permissions) {
      const d = p.domain || 'platform';
      if (!map[d]) map[d] = [];
      map[d].push(p);
    }
    return map;
  }, [permissions]);

  const togglePermission = (code) => {
    setForm((prev) => {
      const has = prev.permissions.includes(code);
      return {
        ...prev,
        permissions: has
          ? prev.permissions.filter((c) => c !== code)
          : [...prev.permissions, code],
      };
    });
  };

  const toggleModule = (key) => {
    setForm((prev) => ({
      ...prev,
      module_access: {
        ...prev.module_access,
        [key]: !prev.module_access?.[key],
      },
    }));
  };

  if (!open) return null;

  return (
    <div className="form-modal-overlay" style={{ zIndex: 9998 }} role="dialog" aria-modal="true" onClick={() => !saving && onClose?.()}>
      <div
        className="form-modal max-w-2xl w-full max-h-[90vh] overflow-y-auto bg-white border border-slate-200 rounded-2xl p-6 space-y-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-lg font-bold text-stone-900">
            {isCreate
              ? t('roles.create_title', 'Nuevo rol')
              : t('roles.edit_title', 'Editar rol')}
          </h3>
          <button type="button" className="p-1 text-stone-500 hover:text-stone-800" onClick={onClose} disabled={saving}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {error ? <div className="alert-error text-sm">{error}</div> : null}

        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block text-sm">
            <span className="font-semibold text-stone-800">{t('roles.field_code', 'Código (code)')}</span>
            <input
              className="input mt-1 w-full font-mono text-sm"
              value={form.code}
              disabled={!isCreate}
              placeholder="admin_soporte"
              onChange={(e) => setForm((prev) => ({
                ...prev,
                code: e.target.value.toLowerCase().replace(/\s+/g, '_'),
              }))}
            />
            {isCreate ? (
              <span className="text-xs text-stone-500 mt-0.5 block">snake_case, único en el sistema</span>
            ) : (
              <span className="text-xs text-stone-500 mt-0.5 block">No editable</span>
            )}
          </label>
          <label className="block text-sm">
            <span className="font-semibold text-stone-800">{t('roles.field_label', 'Nombre visible')}</span>
            <input
              className="input mt-1 w-full"
              value={form.label}
              onChange={(e) => setForm((prev) => ({ ...prev, label: e.target.value }))}
            />
          </label>
          <label className="block text-sm sm:col-span-2">
            <span className="font-semibold text-stone-800">{t('roles.domain', 'Dominio')}</span>
            <select
              className="input mt-1 w-full max-w-xs"
              value={form.domain}
              disabled={!isCreate && form.is_system}
              onChange={(e) => setForm((prev) => ({ ...prev, domain: e.target.value }))}
            >
              {DOMAIN_OPTIONS.map((d) => (
                <option key={d} value={d}>{DOMAIN_LABELS[d] || d}</option>
              ))}
            </select>
          </label>
        </div>

        <fieldset className="border border-slate-200 rounded-xl p-4 space-y-2">
          <legend className="text-sm font-semibold text-stone-800 px-1">{t('users.modules', 'Módulos')}</legend>
          <div className="flex flex-wrap gap-3">
            {MODULE_OPTIONS.map(({ key, label }) => (
              <label key={key} className="inline-flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.module_access?.[key] === true}
                  onChange={() => toggleModule(key)}
                  className="rounded border-stone-300 text-lime-600 focus:ring-lime-500"
                />
                {label}
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset className="border border-slate-200 rounded-xl p-4 space-y-4">
          <legend className="text-sm font-semibold text-stone-800 px-1">{t('roles.permissions', 'Permisos')}</legend>
          {Object.entries(permsByDomain).map(([domain, list]) => (
            <div key={domain}>
              <p className="text-xs font-semibold text-stone-500 uppercase mb-2">
                {DOMAIN_LABELS[domain] || domain}
              </p>
              <div className="flex flex-wrap gap-2">
                {list.map((p) => {
                  const checked = form.permissions.includes(p.code);
                  return (
                    <label
                      key={p.code}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border cursor-pointer transition-colors ${
                        checked
                          ? 'bg-lime-50 border-lime-300 text-lime-900'
                          : 'bg-white border-slate-200 text-stone-600 hover:border-stone-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={checked}
                        onChange={() => togglePermission(p.code)}
                      />
                      {p.label || p.code}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </fieldset>

        <div className="flex gap-3 pt-2">
          <button type="button" className="btn-secondary flex-1" onClick={onClose} disabled={saving}>
            {t('common.cancel', 'Cancelar')}
          </button>
          <button
            type="button"
            className="btn-primary flex-1"
            disabled={saving || !form.label?.trim() || (isCreate && !form.code?.trim())}
            onClick={onSave}
          >
            {saving ? t('common.saving', 'Guardando...') : t('common.save', 'Guardar')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PlatformRolesCatalog({ onBack }) {
  const { t } = useI18n();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [domainFilter, setDomainFilter] = useState('all');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState('edit');
  const [editorForm, setEditorForm] = useState(emptyRoleForm());
  const [editorSaving, setEditorSaving] = useState(false);
  const [editorError, setEditorError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [rolesRes, permsRes] = await Promise.all([
        api.get('/admin/platform-roles'),
        api.get('/admin/platform-permissions'),
      ]);
      setRoles(rolesRes.data?.data || []);
      setPermissions(permsRes.data?.data || []);
    } catch (e) {
      setError(e.response?.data?.error || t('roles.load_error', 'No se pudo cargar el catálogo de roles'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const permByCode = useMemo(
    () => Object.fromEntries(permissions.map((p) => [p.code, p])),
    [permissions],
  );

  const domains = useMemo(
    () => ['all', ...Array.from(new Set(roles.map((r) => r.domain).filter(Boolean)))],
    [roles],
  );

  const filtered = useMemo(() => {
    if (domainFilter === 'all') return roles;
    return roles.filter((r) => r.domain === domainFilter);
  }, [roles, domainFilter]);

  const openCreate = () => {
    setEditorMode('create');
    setEditorForm(emptyRoleForm());
    setEditorError('');
    setEditorOpen(true);
  };

  const openEdit = (role) => {
    setEditorMode('edit');
    setEditorForm(roleToForm(role));
    setEditorError('');
    setEditorOpen(true);
  };

  const closeEditor = () => {
    if (editorSaving) return;
    setEditorOpen(false);
    setEditorError('');
  };

  const saveEditor = async () => {
    setEditorSaving(true);
    setEditorError('');
    try {
      const payload = {
        label: editorForm.label.trim(),
        domain: editorForm.domain,
        permissions: editorForm.permissions,
        module_access: editorForm.module_access,
      };
      if (editorMode === 'create') {
        await api.post('/admin/platform-roles', {
          ...payload,
          code: editorForm.code.trim(),
        });
      } else {
        await api.put(`/admin/platform-roles/${encodeURIComponent(editorForm.code)}`, payload);
      }
      setEditorOpen(false);
      await load();
    } catch (e) {
      setEditorError(e.response?.data?.error || t('roles.save_error', 'No se pudo guardar el rol'));
    } finally {
      setEditorSaving(false);
    }
  };

  return (
    <div className="dashboard-main-view surface-readable d28d-admin-surface space-y-6">
      <header className="flex flex-wrap items-start gap-3 justify-between">
        <div className="flex items-start gap-3">
          {onBack ? (
            <button type="button" className="btn-secondary inline-flex items-center gap-1" onClick={onBack}>
              <ArrowLeft className="w-4 h-4" />
              {t('panel.back_services', 'Volver')}
            </button>
          ) : null}
          <div>
            <h2 className="text-2xl font-bold text-stone-900 flex items-center gap-2">
              <Shield className="w-6 h-6 text-stone-700" />
              {t('roles.catalog_title', 'Roles y permisos')}
            </h2>
            <p className="text-sm text-stone-600 mt-1">
              {t(
                'roles.catalog_subtitle',
                'Catálogo central del ecosistema. Edita permisos por rol o crea roles nuevos. Para asignar un rol a una persona: Usuarios → Editar.',
              )}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="input text-sm max-w-xs"
            value={domainFilter}
            onChange={(e) => setDomainFilter(e.target.value)}
          >
            {domains.map((d) => (
              <option key={d} value={d}>
                {d === 'all' ? t('roles.all_domains', 'Todos los dominios') : (DOMAIN_LABELS[d] || d)}
              </option>
            ))}
          </select>
          <button type="button" className="btn-primary inline-flex items-center gap-1.5 text-sm" onClick={openCreate}>
            <Plus className="w-4 h-4" />
            {t('roles.new_role', 'Nuevo rol')}
          </button>
        </div>
      </header>

      {error ? <div className="alert-error text-sm">{error}</div> : null}

      <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden ui-light-panel">
        {loading ? (
          <p className="p-8 text-sm text-stone-500 text-center">{t('common.loading', 'Cargando...')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-stone-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase w-8" />
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase">{t('common.role', 'Rol')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase">{t('roles.domain', 'Dominio')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase">{t('users.modules', 'Módulos')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase">{t('roles.permissions', 'Permisos')}</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-stone-600 uppercase">{t('common.actions', 'Acciones')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((role) => {
                  const isOpen = expanded === role.code;
                  const permList = Array.isArray(role.permissions) ? role.permissions : [];
                  return (
                    <Fragment key={role.code}>
                      <tr className="hover:bg-stone-50">
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            className="p-1 text-stone-500 hover:text-stone-800"
                            onClick={() => setExpanded(isOpen ? null : role.code)}
                            aria-label={isOpen ? 'Contraer' : 'Expandir'}
                          >
                            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-stone-900">
                          {role.label || role.name}
                          {role.is_system === false ? (
                            <span className="ml-1.5 text-[10px] uppercase tracking-wide text-stone-400">custom</span>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-sm font-mono text-stone-600">{role.code}</td>
                        <td className="px-4 py-3 text-sm text-stone-600">{DOMAIN_LABELS[role.domain] || role.domain || '—'}</td>
                        <td className="px-4 py-3 text-xs text-stone-600 max-w-[200px] truncate" title={moduleSummary(role.module_access)}>
                          {moduleSummary(role.module_access)}
                        </td>
                        <td className="px-4 py-3 text-sm text-stone-700">{permList.length}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            className="admin-action admin-action--neutral inline-flex items-center gap-1"
                            onClick={() => openEdit(role)}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                            {t('common.edit', 'Editar')}
                          </button>
                        </td>
                      </tr>
                      {isOpen ? (
                        <tr key={`${role.code}-detail`} className="bg-slate-50">
                          <td colSpan={7} className="px-6 py-4">
                            <p className="text-xs font-semibold text-stone-500 uppercase mb-2">{t('roles.permissions', 'Permisos')}</p>
                            {permList.length ? (
                              <ul className="flex flex-wrap gap-2">
                                {permList.map((code) => (
                                  <li
                                    key={code}
                                    className="inline-flex px-2 py-0.5 rounded-full text-xs bg-white border border-slate-200 text-stone-700"
                                    title={permByCode[code]?.label || code}
                                  >
                                    {permByCode[code]?.label || code}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-sm text-stone-500">—</p>
                            )}
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-xs text-stone-500">
        {t(
          'roles.catalog_hint',
          'Los roles del sistema (sin etiqueta custom) mantienen su código fijo. Puedes ajustar permisos y módulos. Los roles nuevos solo los asigna super_admin desde Usuarios.',
        )}
      </p>

      <RoleEditorModal
        open={editorOpen}
        mode={editorMode}
        form={editorForm}
        setForm={setEditorForm}
        permissions={permissions}
        saving={editorSaving}
        error={editorError}
        onClose={closeEditor}
        onSave={saveEditor}
      />
    </div>
  );
}
